/**
 * lv-variant-fetch-cache.js
 *
 * Dawn 16 re-renders the product section over the network on every variant
 * change: it disables Add-to-cart / Buy-it-now, fetches
 * "/products/<handle>?section_id=<id>&option_values=<ids>" and only re-enables
 * the buttons once that response arrives. Because Shopify does not edge-cache
 * that render (cf-cache-status: DYNAMIC) the customer waits a full round trip
 * to the Shopify edge - several seconds from far away - staring at a grey
 * button.
 *
 * This script removes that wait without changing Dawn at all:
 *   1. it prefetches the section for every variant, in the background, shortly
 *      after the page becomes interactive (and again on hover / focus);
 *   2. it wraps window.fetch so that, when Dawn asks for a URL we already have,
 *      the stored HTML is returned immediately (and re-warmed for next time).
 *
 * The wrapper is a thin pass-through: anything that is not an exact, cached hit
 * goes straight to the native fetch, and every failure falls back to it too.
 * It only ever stores the product section of this page, for this page's own
 * variant URLs, so it can never answer a request with the wrong product.
 */
(function () {
  'use strict';

  var TTL = 120000;        // reuse a prefetched section for at most 2 minutes
  var MAX_PREFETCH = 10;   // safety cap for large option sets
  var REARM_DELAY = 2000;  // refresh a consumed entry in the background

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) return;

  var cache = new Map();      // url -> { html, at }
  var inflight = new Map();   // url -> Promise

  function productInfo() {
    return document.querySelector('product-info[data-section]');
  }

  function baseUrl() {
    var info = productInfo();
    if (!info || !info.dataset.section || !info.dataset.url) return null;
    return info.dataset.url + '?section_id=' + encodeURIComponent(info.dataset.section);
  }

  function groups(root) {
    return Array.prototype.slice.call(root.querySelectorAll('fieldset, .product-form__input--dropdown'));
  }

  function selectedValueId(group) {
    var element = group.querySelector('input:checked') || group.querySelector('select option[selected]');
    return element && element.dataset ? element.dataset.optionValueId || null : null;
  }

  function prefetchUrls() {
    var root = document.querySelector('variant-selects');
    var base = baseUrl();
    if (!root || !base) return [];

    var allGroups = groups(root);
    var current = allGroups.map(selectedValueId);
    var urls = [];
    var seen = Object.create(null);

    allGroups.forEach(function (group, index) {
      var options = group.querySelectorAll('input[data-option-value-id], select option[data-option-value-id]');
      Array.prototype.forEach.call(options, function (option) {
        var id = option.dataset.optionValueId;
        if (!id) return;
        var ids = current.slice();
        ids[index] = id;
        if (ids.indexOf(null) !== -1) return;
        var url = base + '&option_values=' + ids.join(',');
        if (seen[url]) return;
        seen[url] = true;
        urls.push(url);
      });
    });

    return urls.slice(0, MAX_PREFETCH);
  }

  function prefetch(url) {
    var entry = cache.get(url);
    if (entry && Date.now() - entry.at < TTL) return;
    if (inflight.has(url)) return;

    var promise = nativeFetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.text() : Promise.reject(new Error('HTTP ' + response.status));
      })
      .then(function (text) {
        inflight.delete(url);
        if (text && text.indexOf('<') !== -1) cache.set(url, { html: text, at: Date.now() });
      })
      .catch(function () {
        inflight.delete(url);
      });

    inflight.set(url, promise);
  }

  function warmAll() {
    prefetchUrls().forEach(prefetch);
  }

  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url && url.indexOf('section_id=') !== -1 && cache.has(url)) {
        var entry = cache.get(url);
        if (Date.now() - entry.at < TTL) {
          var signal = init && init.signal;
          if (signal && signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));

          cache.delete(url);
          window.setTimeout(function () {
            prefetch(url);
          }, REARM_DELAY);

          return Promise.resolve(
            new Response(entry.html, {
              status: 200,
              headers: { 'Content-Type': 'text/html; charset=utf-8' },
            })
          );
        }
      }
    } catch (error) {
      // any problem here must never break the real request
    }
    return nativeFetch.apply(this, arguments);
  };

  function onIntent(event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('variant-selects')) return;
    warmAll();
  }

  function init() {
    if (!productInfo() || !document.querySelector('variant-selects')) return;

    document.addEventListener('pointerover', onIntent, true);
    document.addEventListener('focusin', onIntent, true);

    var start = function () {
      window.setTimeout(warmAll, 1200);
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 3000 });
    else window.addEventListener('load', start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
